import React, { useRef, useContext, useEffect, useCallback, useState } from "react";
import { intervalToDuration, isValid, Duration } from "date-fns";
import { RangeDateInput, Description as BaseDescription, InputsGroup, NumberInput, Dropdown } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { timeTickInterval } from "d3-time";
import { ModalHeader, Title, Closeicon, TimeSettings, TimeSettingsItem, SmallFlatButton } from "../styled";
import { DateFilterContext } from "../context";
import { useLayersDateRange } from "../hooks";
import { ActionLayerKind, emptyDuration } from "../reducers";

const getPeriodAndValueFromDuration = (iternval: Duration): [period: keyof Duration, value: number] => {
  const period = Object.keys(iternval).find(key => iternval[key] !== 0) as keyof Duration;
  const value = iternval[period];

  return [period, value];
};

const StepInput = ({ root }) => {
  const { formDurationInterval, setFormDurationInterval, tickCount, formRangeDate } = useContext(DateFilterContext);
  const [initPeriod, initValue] = getPeriodAndValueFromDuration(formDurationInterval);
  const [numberValue, setNumberValue] = useState<number>(initValue);
  const [period, setPeriod] = useState<keyof Duration>(initPeriod);

  useEffect(() => {
    if (
      formRangeDate.every(date => date === null) ||
      tickCount === 0 ||
      Object.keys(formDurationInterval).some(key => formDurationInterval[key] !== 0)
    ) {
      return;
    }
    const interval = timeTickInterval(formRangeDate[0], formRangeDate[1], tickCount);
    const ceilInterval = interval.ceil(new Date());
    const floorInterval = interval.floor(new Date());

    const newDuration = intervalToDuration({ end: ceilInterval, start: floorInterval });

    const [newPeriod, newNumbervalue] = getPeriodAndValueFromDuration(newDuration);

    if (newPeriod) {
      setNumberValue(newNumbervalue);
      setPeriod(newPeriod);
      setFormDurationInterval(newDuration);
    }
  }, [formDurationInterval, formRangeDate, setFormDurationInterval, tickCount]);

  const { t } = useTranslation("dateFilter");

  return (
    <InputsGroup>
      <NumberInput
        value={numberValue}
        onChange={value => {
          setNumberValue(numberValue);
          setFormDurationInterval({
            ...emptyDuration,
            [period]: value,
          });
        }}
        width="4.75rem"
      />
      <Dropdown
        width="9.25rem"
        value={period}
        options={[
          { value: "years", text: t("year") },
          { value: "months", text: t("month") },
          { value: "days", text: t("day") },
          { value: "hours", text: t("hour") },
          { value: "minutes", text: t("minute") },
          { value: "seconds", text: t("second") },
        ]}
        onChange={items => {
          const item = items[0];

          if (item) {
            setPeriod(item.value as keyof Duration);
            setFormDurationInterval({
              ...emptyDuration,
              [item.value]: numberValue,
            });
          }
        }}
        root={root}
      />
    </InputsGroup>
  );
};

export default () => {
  const { setSettingVisibility, layers, formRangeDate, formLayerDispatcher, setFormRangeDate, formLayers, rangeDate } =
    useContext(DateFilterContext);
  const { range, loading } = useLayersDateRange(formLayers);
  const { t } = useTranslation("dateFilter");
  const ref = useRef(null);

  const setRange = useCallback(
    selectDate => {
      if (isValid(selectDate[0]) && isValid(selectDate[1])) {
        setFormRangeDate(selectDate);
      }
    },
    [setFormRangeDate],
  );

  return (
    <ModalHeader ref={ref}>
      <Title>{t("options")}</Title>
      <Closeicon
        kind="close"
        onClick={() => {
          setSettingVisibility(false);
          setRange(rangeDate);
          formLayerDispatcher({ type: ActionLayerKind.AddLayers, payload: layers });
        }}
      />
      <TimeSettings>
        <TimeSettingsItem>
          <BaseDescription>{t("period")}</BaseDescription>
          <RangeDateInput
            width={"33rem"}
            root={ref.current}
            isRange
            withTime
            startDate={formRangeDate[0]}
            endDate={formRangeDate[1]}
            setRangeDate={setRange}
          />
          <SmallFlatButton
            disabled={loading || !range}
            onClick={() => {
              setRange(range);
            }}
            primary
          >
            {t("setMaxRange")}
          </SmallFlatButton>
        </TimeSettingsItem>
        <TimeSettingsItem>
          <BaseDescription>{t("step")}</BaseDescription>
          <StepInput root={ref.current} />
        </TimeSettingsItem>
      </TimeSettings>
    </ModalHeader>
  );
};
